import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    MailIcon, ShieldCheck, Instagram, Twitter, Facebook,
    Linkedin, Youtube, MessageCircle, Send, MapPin, Lock, BadgeCheck, Award,
} from "lucide-react";
import { api } from "@/lib/api";
import { LandingLogo } from "@/components/landing/LandingHeader";
import { useLang } from "@/contexts/LanguageContext";
import { useSiteAsset } from "@/lib/useSiteAsset";
import { useUiLabel } from "@/contexts/UiOverridesContext";
import { useBranding } from "@/contexts/BrandingContext";

const SOCIAL_ICON_MAP = {
    facebook:  Facebook,
    instagram: Instagram,
    linkedin:  Linkedin,
    youtube:   Youtube,
    whatsapp:  MessageCircle,
    twitter:   Twitter,
    telegram:  Send,
};

// Official pages (owner-provided). Admin can edit via Admin → User Page Editor → socials.
const FALLBACK_SOCIAL = [
    { icon: "instagram", label: "Instagram", href: "https://www.instagram.com/jeevanvirasat?igsh=MW9kam00NHVvMjRjbw" },
    { icon: "facebook",  label: "Facebook",  href: "https://www.facebook.com/share/1Ad6zCFAvp/?mibextid=wwXIfr" },
    { icon: "youtube",   label: "YouTube",   href: "https://youtube.com/@jeevanvirasat?si=XRBqnKyW6i6QSj36" },
    { icon: "whatsapp",  label: "WhatsApp",  href: "https://wa.me/919137774777" },
];

const FALLBACK_COMPANY = [
    { label: "Team", target: "/team" },
    { label: "About us", target: "/about" },
    { label: "Founder Letter", target: "/founder-letter" },
    { label: "Vision Mission", target: "/vision" },
    { label: "Made in India", target: "/made-in-india" },
    { label: "Unique Value Proposition", target: "/uvps" },
    { label: "Franchisee", target: "/franchise" },
    { label: "Jobs Opportunities", target: "/careers" },
    { label: "Research and Scholarships", target: "/info/research-and-scholarships" },
];

const FALLBACK_FTSERVICES = [
    { label: "Family Tree", target: "/features#core" },
    { label: "Memories Record", target: "/features#memories" },
    { label: "Audio Video Record", target: "/info/audio-video-record" },
    { label: "Document Vault", target: "/features#vault" },
    { label: "Health Repository", target: "/features#health" },
    { label: "Time Capsule", target: "/time-capsule" },
    { label: "Expert writer Service", target: "/expert-writer-workflow" },
    { label: "Expert Writer Review T&C", target: "/expert-writer-tnc" },
    { label: "Tree merging services", target: "/tree-merging" },
    { label: "Tree Assistance T&C", target: "/tree-assistance-tnc" },
    { label: "Family History Research T&C", target: "/history-research-tnc" },
];

const FALLBACK_SUPPORT = [
    { label: "FAQ system", target: "/faq" },
    { label: "WA - Message", target: "https://wa.me/919137774777", link_type: "external" },
    { label: "Ticket Generation", target: "/support" },
    { label: "AI BOT", target: "/faq#ask" },
    { label: "Escalation matrix", target: "/escalation-matrix" },
    { label: "Account recovery", target: "/account-recovery" },
    { label: "NGO partnership", target: "/ngo-partnership" },
    { label: "Social Media Pages", target: "/info/social-media-pages" },
];

const FALLBACK_LEGAL = [
    { label: "Copyrights", target: "/copyright" },
    { label: "Digital inheritance", target: "/digital-inheritance" },
    { label: "Customer consent", target: "/customer-consent" },
    { label: "Legal disclaimer", target: "/legal-disclaimer" },
    { label: "Data breach response", target: "/data-breach-response" },
    { label: "Customer Awareness Disclaimer", target: "/customer-awareness" },
    { label: "Writer NDA Declaration", target: "/writer-nda" },
    { label: "Legacy Access & Social Impact", target: "/community-service" },
    { label: "Security notice", target: "/security-notice" },
    { label: "User consent ownership", target: "/user-content-ownership" },
    { label: "Nominee access", target: "/nominee-access" },
    { label: "Privacy security", target: "/privacy" },
];

const FALLBACK_ADMINSUPPORT = [
    { label: "Nominee access form", target: "/nominee-form" },
    { label: "Family dispute", target: "/family-dispute" },
    { label: "Transfer on death", target: "/transfer-on-death" },
    { label: "Record Deletion", target: "/record-deletion" },
    { label: "Data export", target: "/data-portability" },
    { label: "Decreased Member Help", target: "/deceased-transition" },
    { label: "disaster recovery", target: "/disaster-recovery" },
];

const FALLBACK_TECHNICAL = [
    { label: "Ethically AI", target: "/ethical-ai" },
    { label: "Data Security", target: "/data-security" },
    { label: "Data protection", target: "/data-protection" },
    { label: "Data encryption", target: "/data-encryption" },
    { label: "DPDP", target: "/dpdp-compliance" },
    { label: "ISO", target: "/data-security#iso" },
    { label: "SOC2", target: "/data-security#soc2" },
    { label: "Bank grade encryption", target: "/data-encryption" },
];

const CERTS = [
    { flag: true, label: "Made in India · Built in Mumbai" },
    { icon: BadgeCheck, label: "DPDP Act 2023 Compliant" },
    { icon: ShieldCheck, label: "ISO 27001 Certified" },
    { icon: Award, label: "SOC 2 Certified" },
    { icon: Lock, label: "Bank-level encryption" },
];

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Newsletter = () => {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const { t } = useLang();

    const submit = (e) => {
        e.preventDefault();
        const trimmed = email.trim();
        if (!trimmed || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
            toast.error("Please enter a valid email address.");
            return;
        }
        setSubmitting(true);
        api.post("/forms/submit", { form_type: "newsletter", fields: { email: trimmed } })
            .then(({ data }) => {
                toast.success(data?.already_subscribed ? "You're already subscribed — thank you!" : "Welcome aboard — we'll send heritage tips, not spam.");
                setEmail("");
            })
            .catch((err) => toast.error(err?.response?.data?.detail || "Could not subscribe. Please try again."))
            .finally(() => setSubmitting(false));
    };

    return (
        <form onSubmit={submit} noValidate className="flex w-full max-w-md items-stretch gap-0 rounded-full overflow-hidden bg-white shadow-[0_10px_28px_rgba(0,0,0,0.14)]" data-testid="footer-newsletter">
            <span className="pl-4 flex items-center text-[#9CA3AF]"><MailIcon className="h-4 w-4" /></span>
            <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email id"
                data-testid="footer-newsletter-input"
                className="flex-1 h-12 px-3 bg-transparent outline-none text-[#1F2937] placeholder:text-[#9CA3AF] text-[14.5px]"
            />
            <button
                type="submit"
                disabled={submitting}
                data-testid="footer-newsletter-submit"
                className="inline-flex items-center gap-1.5 px-6 bg-[#EF4444] hover:bg-[#DC2626] text-white font-semibold text-[14px] transition-colors disabled:opacity-50"
            >
                {submitting ? t("ftJoining") : <>{t("ftSubscribe")} <Send className="h-4 w-4" /></>}
            </button>
        </form>
    );
};

const FooterColumn = ({ heading, testid, items, navOrOpen }) => (
    <div>
        <h4 className="text-[12px] uppercase tracking-[0.16em] font-bold text-[#111827] mb-4">
            {heading}
        </h4>
        <ul className={items.length > 12 ? "block columns-2 gap-x-5 space-y-3" : "flex flex-col gap-2.5"}>
            {items.map((c) => (
                <li key={c.label} className="break-inside-avoid">
                    <button
                        type="button"
                        onClick={() => navOrOpen(c.target, c.link_type)}
                        data-testid={`footer-${testid}-${slugify(c.label)}`}
                        className="text-left text-[13.5px] font-medium text-[#4B5563] hover:text-[#6D28D9] transition-colors leading-snug"
                    >
                        {c.label}
                    </button>
                </li>
            ))}
        </ul>
    </div>
);

export const CtaFooter = () => {
    const navigate = useNavigate();
    const [columns, setColumns] = useState({
        company: FALLBACK_COMPANY,
        ftservices: FALLBACK_FTSERVICES,
        support: FALLBACK_SUPPORT,
        legal:   FALLBACK_LEGAL,
        adminsupport: FALLBACK_ADMINSUPPORT,
        technical:   FALLBACK_TECHNICAL,
    });
    const [socials, setSocials] = useState(FALLBACK_SOCIAL);
    const { support: supportContact } = useBranding();
    const waOverride = supportContact?.whatsapp ? `https://wa.me/${supportContact.whatsapp.replace(/\D/g, "")}` : null;
    const applySupport = (rows) => rows.map((r) => (waOverride && typeof r.target === "string" && r.target.startsWith("https://wa.me/") ? { ...r, target: waOverride } : r));
    const applySocial = (rows) => rows.map((r) => (waOverride && r.icon === "whatsapp" ? { ...r, href: waOverride } : r));
    const footerImage = useSiteAsset("footer.image", null);
    const uiLabel = useUiLabel();
    const { t } = useLang();

    useEffect(() => {
        let cancelled = false;
        api.get("/landing/footer-links").then(({ data }) => {
            if (cancelled || !data) return;
            const next = {
                company: (data.company && data.company.length) ? data.company : FALLBACK_COMPANY,
                ftservices: (data.ftservices && data.ftservices.length)
                    ? data.ftservices
                    : (data.services && data.services.length ? data.services : FALLBACK_FTSERVICES),
                support: (data.support && data.support.length) ? data.support : FALLBACK_SUPPORT,
                legal:   (data.legal && data.legal.length) ? data.legal : FALLBACK_LEGAL,
                adminsupport: (data.adminsupport && data.adminsupport.length) ? data.adminsupport : FALLBACK_ADMINSUPPORT,
                technical:   (data.technical && data.technical.length) ? data.technical : FALLBACK_TECHNICAL,
            };
            setColumns(next);
        }).catch(() => {});
        api.get("/user-pages/socials/landing").then(({ data }) => {
            if (cancelled || !data?.socials?.length) return;
            // Only channels with a real URL are shown (blank URL = kept in admin, hidden here).
            const allowed = ["facebook", "instagram", "linkedin", "youtube", "whatsapp"];
            const filtered = data.socials.filter((s) => allowed.includes(s.icon) && s.href && s.href.trim());
            setSocials(filtered.length ? filtered : FALLBACK_SOCIAL);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const navOrOpen = (target, linkType) => {
        if (linkType === "external") {
            window.open(target, "_blank", "noopener,noreferrer");
        } else if (target && target.includes("#") && !target.startsWith("/#")) {
            const [path, hash] = target.split("#");
            navigate(path || "/");
            setTimeout(() => {
                const el = document.getElementById(hash);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 60);
        } else {
            navigate(target);
        }
    };

    return (
        <footer className="bg-[#F2F2F3] text-[#1F2937]" data-testid="landing-footer">
            {/* Purple band — certifications + newsletter */}
            <div className="bg-[#4F1FD6]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-testid="footer-certs">
                        {CERTS.map((c) => (
                            <div key={c.label} className="flex items-center gap-2.5 text-white text-[14px] font-medium">
                                {c.flag ? (
                                    <span className="inline-flex h-4 w-6 items-stretch overflow-hidden rounded-[2px] border border-white/30 flex-shrink-0">
                                        <span className="h-full w-1/3 bg-[#FF9933]" />
                                        <span className="h-full w-1/3 bg-white" />
                                        <span className="h-full w-1/3 bg-[#138808]" />
                                    </span>
                                ) : (
                                    <c.icon className="h-4.5 w-4.5 text-white/90 flex-shrink-0" />
                                )}
                                <span>{c.label}</span>
                            </div>
                        ))}
                    </div>
                    <div>
                        <p className="text-[15px] text-white/90 leading-relaxed max-w-md">
                            {t("ftNewsDesc")}
                        </p>
                        <div className="mt-5"><Newsletter /></div>
                        <p className="mt-3 text-[12px] text-white/70 inline-flex items-center gap-1.5">
                            <ShieldCheck className="h-3.5 w-3.5" /> {t("ftDpdpNote")}
                        </p>
                    </div>
                </div>
            </div>

            {/* Light column grid */}
            <div className="relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-8 relative z-10">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-12 gap-x-6">
                        <FooterColumn heading={uiLabel("footer.col.company", t("colCompany"))} testid="company" items={columns.company} navOrOpen={navOrOpen} />
                        <FooterColumn heading={uiLabel("footer.col.ftservices", t("colFtServices"))} testid="ftservices" items={columns.ftservices} navOrOpen={navOrOpen} />
                        <FooterColumn heading={uiLabel("footer.col.support", t("colSupport"))} testid="support" items={applySupport(columns.support)} navOrOpen={navOrOpen} />
                        <FooterColumn heading={uiLabel("footer.col.legal", t("colLegal"))} testid="legal" items={columns.legal} navOrOpen={navOrOpen} />
                        <FooterColumn heading={uiLabel("footer.col.adminsupport", t("colAdminSupport"))} testid="adminsupport" items={columns.adminsupport} navOrOpen={navOrOpen} />
                        <FooterColumn heading={uiLabel("footer.col.technical", t("colTechnical"))} testid="technical" items={columns.technical} navOrOpen={navOrOpen} />
                    </div>

                    <div className="mt-12 h-px w-full bg-[#D8D8DC]" />

                    {/* Brand row — admin-uploaded image LEFT (blank white until uploaded), text RIGHT */}
                    <div className="pt-8 grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
                        <div className="md:col-span-4">
                            <div
                                className="w-full aspect-[16/10] rounded-2xl bg-white border border-[#E2E2E6] overflow-hidden"
                                data-testid="footer-image-area"
                                data-has-image={footerImage.url ? "true" : "false"}
                            >
                                {footerImage.url && (
                                    <img
                                        src={footerImage.url}
                                        alt={footerImage.alt || "Family Repository"}
                                        className="w-full h-full object-contain"
                                        data-testid="footer-image"
                                    />
                                )}
                            </div>
                        </div>
                        <div className="md:col-span-4">
                            <LandingLogo />
                            <p className="mt-4 text-[13.5px] text-[#4B5563] leading-relaxed max-w-lg">
                                A Private, India-First Space Where Families Preserve Their Stories, Record Elders' Voices, And Pass On Their Legacy Across Generations.
                            </p>
                        </div>
                        <div className="md:col-span-4 flex flex-col md:items-end gap-4">
                            <div className="space-y-2">
                                <a
                                    href="mailto:Support@familyrepository.org"
                                    data-testid="footer-contact-email"
                                    className="inline-flex items-center gap-2 text-[14px] font-semibold text-[#1F2937] hover:text-[#6D28D9] transition-colors underline underline-offset-2"
                                >
                                    Support@familyrepository.org
                                </a>
                                <div className="flex items-center gap-2 text-[13px] text-[#4B5563]">
                                    <MapPin className="h-4 w-4 text-[#6D28D9]" /> Mumbai, Maharashtra, India
                                </div>
                            </div>
                            <div className="flex items-center gap-2.5" data-testid="footer-socials">
                                {applySocial(socials).map((s) => {
                                    const Icon = SOCIAL_ICON_MAP[s.icon] || Instagram;
                                    return (
                                        <a
                                            key={s.icon + s.href}
                                            href={s.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={s.label}
                                            data-testid={`footer-social-${s.icon}`}
                                            className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#1F2937] text-white hover:bg-[#6D28D9] transition-all"
                                        >
                                            <Icon className="h-4 w-4" />
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Copyright */}
            <div className="border-t border-[#E2E2E6]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-3">
                    <p className="text-[12px] text-[#6B7280] font-medium" data-testid="footer-copyright">
                        © 2026 Humanity First Software Technology Pvt. Ltd — All Rights Reserved · Designed &amp; Developed In India
                    </p>
                </div>
            </div>
        </footer>
    );
};
